import type { ForecastTraderElectionGridMatchedContract } from '@/types/forecasttrader.types';
import type { PositionData } from '@/types/accountPnl.types';

/**
 * Anything carrying a candidate's primary contract conids plus its `matched`
 * siblings on other exchanges. A position may be held on any of these venues,
 * so position lookups must consider all of them — not just the primary.
 */
export interface MatchableContract {
  conid_yes: number;
  conid_no: number;
  matched?: ForecastTraderElectionGridMatchedContract[];
}

/** All "yes" conids for a candidate across the primary and matched exchanges. */
export function allYesConids(contract: MatchableContract): number[] {
  const conids = [contract.conid_yes];
  for (const m of contract.matched ?? []) conids.push(m.conid_yes);
  return conids;
}

/** All "no" conids for a candidate across the primary and matched exchanges. */
export function allNoConids(contract: MatchableContract): number[] {
  const conids = [contract.conid_no];
  for (const m of contract.matched ?? []) conids.push(m.conid_no);
  return conids;
}

type PositionsMap = Record<number, PositionData> | undefined | null;

function hasOpenPosition(conids: number[], positions: PositionsMap): boolean {
  if (!positions) return false;
  return conids.some((conid) => {
    const pos = positions[conid];
    return !!(pos && pos.position !== 0);
  });
}

/** True when the candidate holds an open "yes" position on any exchange. */
export function hasYesPosition(contract: MatchableContract, positions: PositionsMap): boolean {
  return hasOpenPosition(allYesConids(contract), positions);
}

/** True when the candidate holds an open "no" position on any exchange. */
export function hasNoPosition(contract: MatchableContract, positions: PositionsMap): boolean {
  return hasOpenPosition(allNoConids(contract), positions);
}

/** Position side held for this candidate across all exchanges, or null when flat. */
export function getMatchedPositionSide(contract: MatchableContract, positions: PositionsMap): 'Yes' | 'No' | null {
  if (hasYesPosition(contract, positions)) return 'Yes';
  if (hasNoPosition(contract, positions)) return 'No';
  return null;
}

/**
 * Extracts the numeric conids from `conid@exchange` pair strings, falling back to the
 * single primary conid when no cross-exchange pairs are present. Used by detail-view
 * candidates whose matched venues are carried as pair strings rather than a `matched[]`.
 */
export function conidsFromPairs(pairs: string[] | undefined, fallback: number): number[] {
  if (!pairs || pairs.length === 0) return [fallback];
  return pairs.map((p) => Number(p.split('@')[0])).filter((n) => !Number.isNaN(n));
}

/** True when any of the pair-encoded conids holds an open position. */
export function hasPositionInPairs(pairs: string[] | undefined, fallback: number, positions: PositionsMap): boolean {
  return hasOpenPosition(conidsFromPairs(pairs, fallback), positions);
}
